import React from "react";

import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Textarea,
  Stack,
  StackDivider,
  Flex,
  Avatar,
  Text,
} from "@chakra-ui/react";
import FormDescription from "../../../utils/chakra/FormDescription";

import useTranslations from "../../../../hooks/useTranslations";
import useStore from "../../../../store";
import useTextInputFocus from "../../hooks/useTextInputFocus";
import useQuestionErrors from "../../hooks/useQuestionErrors";
import usePreviousAnswers from "../../hooks/usePreviousAnswers";

import AnswerEmpty from "../answers/AnswerEmpty";
import AnswerTextReview from "../answers/AnswerTextReview";

import QuestionActions from "./QuestionActions";
import QuestionCommonInputs from "./QuestionCommonInputs";

import { AnswerFragment } from "../../../../graphql/generated/definitions";
import { QuestionProps } from "./types/QuestionProps";

const QuestionText: React.FC<QuestionProps> = ({
  register,
  path,
  control,
  question,
  report,
  ...restProps
}) => {
  const t = useTranslations();
  const viewer = useStore((state) => state.viewer);
  const { onFocus, onBlur } = useTextInputFocus();
  const previousAnswers = usePreviousAnswers(
    question,
    report
  ) as AnswerFragment[];
  const { isInvalid, errorMessage } = useQuestionErrors({
    control,
    name: `${path}.value`,
  });

  const { onBlur: registerBlur, ...registerMethods } = register(
    `${path}.value` as const,
    {
      required:
        question.required && (t("reports.shared.requiredQuestion") as string),
    }
  );

  return (
    <Box>
      <FormControl
        isInvalid={isInvalid}
        isRequired={question.required}
        id={`${path}.value`}
      >
        <FormLabel fontWeight="semibold" fontSize="xl">
          {restProps.sectionIndex + 1}.{restProps.questionIndex + 1}
          {" • "}
          {question.label}
        </FormLabel>
        {question.description && (
          <FormDescription>{question.description}</FormDescription>
        )}
        <QuestionActions {...{ question, report }} />
        <Stack spacing="7" divider={<StackDivider maxW="2xl" />}>
          {previousAnswers.map((answer) =>
            answer.isEmpty ? (
              <AnswerEmpty
                key={answer.id}
                path={path}
                answer={answer}
                question={question}
              />
            ) : (
              <AnswerTextReview
                key={answer.id}
                path={path}
                answer={answer}
                question={question}
              />
            )
          )}
          <Stack spacing="4">
            {previousAnswers.length > 0 && (
              <Stack spacing="3" alignItems="end" direction="row">
                <Avatar
                  size="sm"
                  src={viewer.avatar}
                  alt={viewer.name}
                  name={viewer.name}
                />
                <Text fontWeight="medium" fontSize="md" color="gray.600">
                  {t("reports.edit.yourAnswer")}
                </Text>
              </Stack>
            )}
            <Flex direction="column">
              <Textarea
                // This looks rather ugly but native form validation block the one made by RHF
                isRequired={false}
                aria-required={question.required ? "true" : "false"}
                placeholder={
                  t("reports.edit.questionTextPlaceholder") as string
                }
                onFocus={onFocus()}
                onBlur={onBlur(registerBlur)}
                {...registerMethods}
              />
              <FormErrorMessage color="invalid.500">
                {errorMessage}
              </FormErrorMessage>
              <QuestionCommonInputs
                config={{
                  hasComment: false,
                  hasTimeTracked: question.timeTracked,
                }}
                {...{
                  register,
                  path,
                  control,
                  question,
                  report,
                  ...restProps,
                }}
              />
            </Flex>
          </Stack>
        </Stack>
      </FormControl>
    </Box>
  );
};

export default React.memo(QuestionText);
